import { MouseEvent, useRef } from "react";
import { BoxProps, Flex } from "@chakra-ui/react";
import dynamic from "next/dynamic";
import { useTranslation } from "next-i18next";
import { IPortfolioChartDict } from "src/api/server/wf-detail.types";
import { Section } from "src/components/base";
import { WfHeading } from "src/components/base/wf-heading";
import { ArrowLink } from "src/components-mui/base/arrow-link/arrow-link";
import { useJumpMarksNavigation } from "src/components-mui/common/jump-marks/jump-marks.context";
import { useIsSectionInView } from "src/components-mui/common/jump-marks/use-is-section-in-view";
import { DetailJumpMark } from "src/components-mui/pages/detail/detail-jump-marks";
import { LoadingState } from "./loading-state";

interface IPortfolioChartProps extends BoxProps {
    wikifolioId: string;
    dict: IPortfolioChartDict;
}

const PortfolioChart = dynamic<IPortfolioChartProps>(
    () => import("src/components/pages/detail/portfolio-chart/portfolio-chart").then(component => component.PortfolioChart),
    {
        loading: () => <LoadingState />,
    }
);

export const PortfolioChartWrapper = ({ wikifolioId, dict, ...boxProps }: IPortfolioChartProps) => {
    const { t } = useTranslation("common");
    const sectionRef = useRef<HTMLDivElement>(null);
    const isVisible = useIsSectionInView({ sectionRef, visibleOnce: true });
    const { navigateToSection } = useJumpMarksNavigation();

    const handleDetailsClick = (event: MouseEvent<HTMLAnchorElement>) => {
        event.preventDefault();
        navigateToSection(DetailJumpMark.Portfolio);
    };

    return (
        <Section ref={sectionRef} pos="relative" {...boxProps}>
            <Flex justify="space-between" align="center">
                <WfHeading as="h2" fontSize="2xl">
                    {dict.title}
                </WfHeading>
                <ArrowLink href={`#${DetailJumpMark.Portfolio}`} onClick={handleDetailsClick} sx={{ fontSize: "md" }}>
                    {t("details")}
                </ArrowLink>
            </Flex>
            {isVisible ? <PortfolioChart wikifolioId={wikifolioId} dict={dict} {...boxProps} /> : <LoadingState />}
        </Section>
    );
};
